// === AiWorkflowGenerator === Round 32 / Workflow Architect UI entry point.
//
// Lives in the CanvasWorkflowPanel header next to Auto-layout. Renders an
// icon-only "auto_awesome" trigger button; when clicked, expands into a
// small inline popover anchored under the button with:
//   - a textarea for the topic prompt
//   - a Generate button
//
// On submit it dispatches generateWorkflowFromPromptThunk(sectionId, prompt,
// locale), which calls the middleware Workflow Architect endpoint and
// materializes the returned items + triggers into the active scene's
// InteractivePlugin box and the section workflow.
//
// The popover is NOT a modal — per the project rule "no popup over a popup"
// (CanvasWorkflowPanel can be hosted inside InteractivityStudio's modal),
// this is a small floating panel that closes on Escape, Backdrop click, or
// after successful submit. Focus management is intentionally lightweight:
// autofocus on open, return focus to the trigger on close.
//
// Locale is read from window.idealStudioLocale / i18n if available, falling
// back to "en". The thunk forwards whatever we pass through verbatim.

import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { generateWorkflowFromPromptThunk } from "../../../common/actions";
import { LuSparkles } from "react-icons/lu";
import "./AiWorkflowGenerator.scss";
import { getContentLanguage } from "../../../common/content_language";

function resolveLocale() {
  return getContentLanguage();
}

export function AiWorkflowGenerator({ sectionId, onGenerate }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const triggerRef = useRef(null);
  const textareaRef = useRef(null);
  const popoverRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setError(null);
    if (triggerRef.current && typeof triggerRef.current.focus === "function") {
      triggerRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    if (textareaRef.current && typeof textareaRef.current.focus === "function") {
      textareaRef.current.focus();
    }
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    const onClick = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, close]);

  const disabled = !sectionId && sectionId !== 0;

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const trimmed = prompt.trim();
    if (!trimmed || busy || disabled) return;
    setBusy(true);
    setError(null);
    try {
      await onGenerate(sectionId, trimmed, resolveLocale());
      setPrompt("");
      setOpen(false);
      if (triggerRef.current && typeof triggerRef.current.focus === "function") {
        triggerRef.current.focus();
      }
    } catch (err) {
      setError(String((err && err.message) || err) || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ai-workflow-generator">
      <button
        ref={triggerRef}
        type="button"
        className="ai-workflow-generator__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label="Generate from prompt"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-disabled={disabled}
        disabled={disabled}
        title="Generate from prompt"
        data-testid="ai-workflow-generator-trigger"
      >
        <LuSparkles size={16} />
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="ai-workflow-generator__popover"
          role="dialog"
          aria-label="Generate workflow from prompt"
          data-testid="ai-workflow-generator-popover"
        >
          <form className="ai-workflow-generator__form" onSubmit={handleSubmit}>
            <label className="ai-workflow-generator__label" htmlFor="ai-workflow-generator-input">
              Generate from prompt
            </label>
            <textarea
              ref={textareaRef}
              id="ai-workflow-generator-input"
              className="ai-workflow-generator__textarea"
              rows={4}
              placeholder="e.g. Onboarding checklist for new hires"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={busy}
              data-testid="ai-workflow-generator-input"
            />
            {error && (
              <div className="ai-workflow-generator__error" role="alert">
                {error}
              </div>
            )}
            <div className="ai-workflow-generator__actions">
              <button
                type="button"
                className="ai-workflow-generator__cancel"
                onClick={close}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ai-workflow-generator__submit"
                disabled={busy || !prompt.trim() || disabled}
                data-testid="ai-workflow-generator-submit"
              >
                {busy ? "Generating…" : "Generate"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const mapDispatch = (dispatch) => ({
  onGenerate: (sectionId, prompt, locale) =>
    dispatch(generateWorkflowFromPromptThunk(sectionId, prompt, locale)),
});

export default connect(null, mapDispatch)(AiWorkflowGenerator);
